import React, { useState } from 'react';
import { Stethoscope, Zap, Copy, Check, ChevronDown, ChevronRight, AlertCircle, ArrowRight } from 'lucide-react';
import { Button, LoadingSpinner } from '@/components/ui';
import { usePromptDoctor } from '../usePromptDoctor';
import { findLostSignals } from '../lostSignals';
import { analyzePrompt } from '../../SunoReadiness/rulesEngine';
import type { ReadinessInput } from '../../SunoReadiness/types';
import type { SunoPrepPayload } from '@components/SunoPrep/SunoPrepModal';

interface PromptDoctorPanelProps {
  styleText: string;
  lyricsText: string;
  blends: string[];
  openSunoPrep?: (payload?: Partial<SunoPrepPayload>) => void;
}

export function PromptDoctorPanel({ styleText, lyricsText, blends, openSunoPrep }: PromptDoctorPanelProps): React.ReactElement | null {
  const { result, isRunning, error, run, clear } = usePromptDoctor();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!styleText.trim()) return null;

  const input: ReadinessInput = { styleText, lyricsText, blends };

  const handleRun = async () => {
    setExpanded(true);
    await run(input);
  };

  const handleCopy = async () => {
    if (!result?.fixedStyle) return;
    await navigator.clipboard.writeText(result.fixedStyle);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendToSuno = () => {
    if (!result?.fixedStyle || !openSunoPrep) return;
    openSunoPrep({ tags: result.fixedStyle });
  };

  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40 transition-colors text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Stethoscope size={14} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-slate-200">Prompt Doctor</span>
          {result && !isRunning && (
            <span className="ml-2 text-[11px] text-emerald-400 font-medium">Fix ready</span>
          )}
          {error && !isRunning && (
            <span className="ml-2 text-[11px] text-red-400">Error</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isRunning && (
            <Button
              size="xs"
              variant={result ? 'ghost' : 'primary'}
              onClick={e => { e.stopPropagation(); handleRun(); }}
              leftIcon={<Zap size={11} />}
              className={result ? 'text-emerald-400 border-emerald-500/40 hover:border-emerald-400' : ''}
            >
              {result ? 'Re-diagnose' : 'Diagnose & Fix'}
            </Button>
          )}
          {expanded ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div className="border-t border-slate-700/40 px-4 py-4 space-y-4">

          {/* Loading */}
          {isRunning && (
            <div className="flex items-center gap-3 py-4 text-sm text-slate-400">
              <LoadingSpinner size="sm" />
              <span>Diagnosing prompt…</span>
            </div>
          )}

          {/* Error */}
          {error && !isRunning && (
            <div className="flex items-start gap-2 rounded-lg bg-red-950/30 border border-red-500/30 p-3">
              <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && !isRunning && (
            <>
              {/* Diagnosis */}
              <div className="rounded-lg bg-amber-950/20 border border-amber-500/25 p-3">
                <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1.5">Diagnosis</p>
                <p className="text-sm text-slate-300 leading-relaxed">{result.diagnosis}</p>
              </div>

              {/* Changes */}
              {result.changes.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Changes Made</p>
                  <ul className="space-y-1.5">
                    {result.changes.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <ArrowRight size={11} className="text-emerald-400 mt-0.5 shrink-0" />
                        <span>
                          <span className="text-slate-200 font-medium">{c.what}</span>
                          <span className="text-slate-500"> — </span>
                          <span className="text-slate-400">{c.why}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Fixed style */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Fixed Style</p>
                <div className="rounded-lg bg-slate-800/60 border border-slate-600/40 p-3">
                  <p className="text-sm text-emerald-300 font-mono leading-relaxed break-words">{result.fixedStyle}</p>
                </div>
              </div>

              {/* What the rewrite dropped (AUTO-098) */}
              {(() => {
                const lost = findLostSignals(styleText, result.fixedStyle, analyzePrompt(input).conflictingGenresFound);
                if (!lost.any) return null;
                const parts: string[] = [];
                if (lost.bpm) parts.push(`the ${lost.bpm} BPM`);
                if (lost.genres.length > 0) parts.push(`genre${lost.genres.length > 1 ? 's' : ''} ${lost.genres.join(', ')}`);
                if (lost.structure.length > 0) parts.push(`structure cue${lost.structure.length > 1 ? 's' : ''} ${lost.structure.join(', ')}`);
                return (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-950/30 border border-amber-500/30 p-3">
                    <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-200">
                      This rewrite no longer mentions {parts.join('; ')}. Check it against your original before using it.
                    </p>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  leftIcon={copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                  className={copied ? 'border-emerald-500/50 text-emerald-400' : ''}
                >
                  {copied ? 'Copied!' : 'Copy Style'}
                </Button>
                {openSunoPrep && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleSendToSuno}
                    leftIcon={<Zap size={13} />}
                  >
                    Send to Suno
                  </Button>
                )}
                <button
                  onClick={() => { clear(); }}
                  className="ml-auto text-xs text-slate-600 hover:text-slate-400 transition-colors"
                >
                  Clear
                </button>
              </div>
            </>
          )}

          {/* Idle — no result yet, panel open */}
          {!result && !isRunning && !error && (
            <p className="text-xs text-slate-500 py-2">
              Click <span className="text-emerald-400 font-medium">Diagnose & Fix</span> to analyze your style prompt and get a rewritten version ready for Suno.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
